'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject
} from 'react';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';

import { IMessenger } from 'app/interfaces/common';
import { IconMessage } from 'app/components/sprites';

export default function MessengersDropdown({
  messengers,
  isHovered
}: {
  messengers: IMessenger[];
  isHovered: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  function onMouseEnter() {
    if (window.innerWidth < 1280) return;
    setIsOpen(true);
  }

  function onMouseLeave() {
    if (window.innerWidth < 1280) return;
    setIsOpen(false);
  }

  // Закрываем при смене маршрута
  useEffect(() => {
    close();
  }, [pathname, close]);

  // Закрываем по клику вне (для тач-устройств)
  // usehooks-ts@3.1.0 ещё ждёт RefObject<T>, а в типах React 19 useRef(null)
  // даёт RefObject<T | null> — каст снимает несоответствие.
  useOnClickOutside(ref as RefObject<HTMLDivElement>, close);

  if (!messengers.length) return null;

  // Элементы списка повторяют ширину триггера (узкие в норме, w-32 при
  // раскрытом меню), высота фиксированная.
  const messengerButtonClassName = classNames(
    'button rounded bg-green transition-all duration-400 h-12 xl:h-16',
    {
      'w-12 xl:w-16': !isHovered,
      'w-32': isHovered
    }
  );

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        aria-label="Мессенджеры"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(isOpen => !isOpen)}
        className={classNames(
          'button rounded bg-green transition-all duration-400 h-full',
          {
            'w-12 xl:w-16': !isHovered,
            'w-32': isHovered
          }
        )}
      >
        <IconMessage />
      </button>

      <div
        className={classNames(
          'absolute right-0 top-full pt-2 flex flex-col gap-2 transition-all duration-300',
          {
            'opacity-0 -translate-y-1 pointer-events-none': !isOpen,
            'opacity-100 translate-y-0': isOpen
          }
        )}
      >
        {messengers.map((messenger, index) => (
          <a
            key={index}
            href={messenger.url}
            target="_blank"
            rel="nofollow"
            className={messengerButtonClassName}
          >
            {!!messenger.icon?.url && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={messenger.icon.url}
                alt={messenger.name}
                className="w-8 h-8 object-contain"
              />
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
